import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useInView } from 'motion/react'

export const SLIDE_TIME = 5000
export const useSlider = (maxSize, no_auto = false) => {
  const [currentSlide, setSlide] = useState(0)
  const TIMEOUT_ID = useRef(null)
  const ref = useRef(null)
  const isInView = useInView(ref, { margin: '0px' })
  const hasStarted = useRef(false)

  const nSlides = useMemo(() => maxSize, [maxSize])
  const tickRef = useRef<() => void>(null)

  const tick = useCallback(() => {
    setSlide(prev => {
      const next = (prev + 1) % nSlides

      if (TIMEOUT_ID.current) clearTimeout(TIMEOUT_ID.current)
      TIMEOUT_ID.current = setTimeout(() => tickRef.current?.(), SLIDE_TIME)

      return next
    })
  }, [nSlides])

  useEffect(() => { tickRef.current = tick }, [tick])

  const stopAnimation = useCallback(() => {
    if (TIMEOUT_ID.current) {
      clearTimeout(TIMEOUT_ID.current)
      TIMEOUT_ID.current = null
    }
  }, [])

  const startAnimation = useCallback(() => {
    stopAnimation()
    TIMEOUT_ID.current = setTimeout(tick, SLIDE_TIME)
  }, [tick, stopAnimation])

  const nextSlide = useCallback(() => {
    tick()
  }, [tick])

  const prevSlide = useCallback(() => {
    setSlide(prev => {
      const newIndex = (prev - 1 + nSlides) % nSlides
      return newIndex
    })
  }, [nSlides])

  useEffect(() => {
    if (isInView && !no_auto && !hasStarted.current) {
      hasStarted.current = true
      startAnimation()
    }

    return () => stopAnimation()
  }, [isInView, no_auto, startAnimation, stopAnimation])

  return { currentSlide, triggerRef: ref, nextSlide, prevSlide, setSlide, startAnimation, stopAnimation }
}